<template>
  <div class="digital-assistant-wrapper">
    <!-- Popup Mode -->
    <template v-if="!fullPage">
      <!-- Chat Toggle Button -->
      <v-btn
        v-if="!showChat"
        fab
        fixed
        bottom
        left
        color="orange"
        class="chat-toggle-btn"
        @click="toggleChat"
        :style="{ bottom: '20px', zIndex: 9999 }"
      >
        <v-avatar size="40">
          <img :src="userAvatar" :alt="userName" style="border-radius: 50%;" />
        </v-avatar>
      </v-btn>

      <!-- Popup Chat Window -->
      <div
        v-if="showChat"
        class="chat-window popup-mode"
        :style="{
          position: 'fixed',
          bottom: '20px',
          right: '80px',
          width: '350px',
          height: '600px',
          zIndex: 9999,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }"
      >
        <v-card style="height: 100%; display: flex; flex-direction: column;">
          <!-- Popup Chat Header -->
          <v-card-title class="chat-header orange darken-1 white--text pa-3 flex-shrink-0">
          <v-avatar size="32" class="mr-2">
            <img :src="userAvatar" :alt="userName" />
          </v-avatar>
          <div class="flex-grow-1">
            <div class="font-weight-bold">{{ userName }}'s Digital Assistant</div>
          </div>
          <v-btn icon small @click="toggleChat" class="ml-2">
            <v-icon color="white" class="material-icons-round">close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Financing Form Option (Post-Agreement) - At the top -->
        <div v-if="showFinancingOption" class="financing-option-top pa-2" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-bottom: 1px solid #e0e0e0;">
          <v-btn
            color="white"
            elevation="2"
            block
            small
            @click="openFinancingModal"
            class="financing-btn"
            style="color: #667eea; font-weight: bold; text-transform: none; border-radius: 6px;"
          >
            Continue with Dealer Financing
          </v-btn>
        </div>

      <!-- Chat Messages -->
      <v-card-text class="chat-messages pa-0 flex-grow-1" ref="chatMessages" style="overflow: hidden;">
        <div class="messages-container pa-3" :style="showFinancingOption ? 'height: 300px; overflow-y: auto;' : 'height: 360px; overflow-y: auto;'">
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="message-wrapper mb-3"
            :class="message.sender_type === 'user' ? 'user-message' : 'assistant-message'"
          >
            <div class="d-flex" :class="message.sender_type === 'user' ? 'justify-end' : 'justify-start'">
              <v-avatar
                v-if="message.sender_type === 'assistant'"
                size="24"
                class="mr-2 mt-1"
              >
                <img :src="userAvatar" :alt="userName" />
              </v-avatar>
              
              <div
                class="message-bubble pa-2 rounded"
                :class="{
                  'blue darken-1 white--text': message.sender_type === 'user',
                  'grey lighten-4': message.sender_type === 'assistant'
                }"
                style="max-width: 80%;"
              >
                <div class="message-text" v-if="message.sender_type === 'assistant'" v-html="formatMessage(message.message)"></div>
                <div class="message-text" v-else>{{ message.message }}</div>
                <div class="message-time caption mt-1" :class="message.sender_type === 'user' ? 'white--text' : 'grey--text'">
                  {{ formatTime(message.sent_at) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Typing Indicator -->
          <div v-if="isTyping" class="d-flex justify-start mb-3">
            <v-avatar size="24" class="mr-2 mt-1">
              <img :src="userAvatar" :alt="userName" />
            </v-avatar>
            <div class="typing-indicator grey lighten-4 pa-2 rounded">
              <div class="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>

          <!-- Voice Call Transcript (Popup Mode) -->
          <div v-if="voiceMode === 'voice' && webCallTranscript.length > 0" class="voice-transcript mt-3">
            <v-divider class="mb-2"></v-divider>
            <h6 class="text-subtitle-2 font-weight-bold mb-2 orange--text">
              <v-icon small class="mr-1 material-icons-round">record_voice_over</v-icon>
              Live Voice Transcript
            </h6>
            <div class="transcript-container" style="overflow-y: auto;">
              <div v-for="(message, index) in webCallTranscript" :key="index" class="transcript-message mb-1">
                <div class="d-flex align-start">
                  <v-chip
                    :color="message.role === 'agent' ? 'orange' : 'blue'"
                    text-color="white"
                    x-small
                    class="mr-2"
                  >
                    {{ message.role === 'agent' ? 'AI' : 'You' }}
                  </v-chip>
                  <span class="text-caption">{{ message.content }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Voice Mode Toggle (Popup Mode) -->
      <div v-if="hasVoiceAgent" class="voice-mode-toggle pa-2 flex-shrink-0" style="background-color: #f0f0f0; border-top: 1px solid #e0e0e0;">
        <v-btn-toggle
          v-model="voiceMode"
          mandatory
          dense
          class="d-flex"
          style="width: 100%;"
        >
          <v-btn
            value="chat"
            small
            class="flex-grow-1"
            :class="{ 'orange white--text': voiceMode === 'chat' }"
          >
            <v-icon left small class="material-icons-round">chat</v-icon>
            Chat
          </v-btn>
          <v-btn
            value="voice"
            small
            class="flex-grow-1"
            :class="{ 'orange white--text': voiceMode === 'voice' }"
          >
            <v-icon left small class="material-icons-round">mic</v-icon>
            Voice
          </v-btn>
        </v-btn-toggle>
      </div>

      <!-- Chat Input -->
      <v-divider class="flex-shrink-0"></v-divider>
      <v-card-actions class="chat-input pa-3 flex-shrink-0" style="background-color: #f8f9fa;">
        <!-- Voice Mode Controls -->
        <div v-if="voiceMode === 'voice' && hasVoiceAgent" class="voice-controls d-flex align-center" style="width: 100%;">
          <div v-if="!isWebCallActive" class="d-flex flex-column" style="width: 100%;">
            <div class="d-flex align-center mb-2" style="width: 100%;">
              <v-btn
                color="green"
                dark
                @click="startWebCall"
                :loading="webCallLoading"
                class="mr-2"
                style="border-radius: 20px;"
              >
                <v-icon left class="material-icons-round">call</v-icon>
                Start Voice Chat
              </v-btn>
              <!-- <v-btn
                color="blue"
                outlined
                small
                @click="testMicrophone"
                :loading="micTestLoading"
                class="mr-2"
                style="border-radius: 15px;"
              >
                <v-icon left small class="material-icons-round">mic</v-icon>
                Test Mic
              </v-btn> -->
              <span class="text-caption grey--text">Click to start voice conversation</span>
            </div>
            
          </div>

          <div v-else class="d-flex align-center justify-space-between" style="width: 100%;">
            <div class="d-flex align-center">
              <v-chip
                :color="isAgentSpeaking ? 'orange' : 'blue'"
                text-color="white"
                small
                class="mr-2"
              >
                <v-icon left x-small class="material-icons-round">
                  {{ isAgentSpeaking ? 'record_voice_over' : 'mic' }}
                </v-icon>
                {{ isAgentSpeaking ? 'AI Speaking' : 'Listening' }}
              </v-chip>
              <v-chip color="green" text-color="white" small>
                <v-icon left x-small class="material-icons-round">phone_in_talk</v-icon>
                Active
              </v-chip>
            </div>

            <v-btn
              color="red"
              dark
              small
              @click="stopWebCall"
              style="border-radius: 20px;"
            >
              <v-icon left small class="material-icons-round">call_end</v-icon>
              End Call
            </v-btn>
          </div>
        </div>

        <!-- Chat Mode Controls -->
        <div v-else class="chat-controls d-flex align-center" style="width: 100%;">
          <v-text-field
            v-model="userInput"
            placeholder="Type your message..."
            outlined
            dense
            hide-details
            @keyup.enter="sendMessage"
            :disabled="isTyping || (isAgreementRequired && !informationCollectionState.isCollecting)"
            class="mr-2"
            style="background-color: white;"
            color="orange"
          ></v-text-field>
          <v-btn
            icon
            color="orange"
            @click="sendMessage"
            :disabled="!userInput.trim() || isTyping || (isAgreementRequired && !informationCollectionState.isCollecting)"
            class="elevation-2"
          >
            <v-icon class="material-icons-round">send</v-icon>
          </v-btn>
        </div>
      </v-card-actions>
        </v-card>
      </div>
    </template>

    <!-- Full Page Mode -->
    <template v-else>
      <div class="chat-window full-page-mode">
        <!-- Full Page Chat Messages -->
        <v-card class="chat-card" elevation="0" style="height: 100%; border-radius: 12px; background: rgba(255, 255, 255, 0.95); backdrop-filter: blur(10px);">
          <!-- Financing Form Option (Post-Agreement) - Full Page Mode -->
          <div v-if="showFinancingOption" class="financing-option-top-fullpage pa-3" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);">
            <v-btn
              color="white"
              elevation="3"
              block
              @click="openFinancingModal"
              class="financing-btn-fullpage"
              style="color: #667eea; font-weight: bold; text-transform: none; border-radius: 8px;"
            >
              GET PRE-QUALIFIED
            </v-btn>
          </div>

          <v-card-text class="chat-messages pa-0" ref="chatMessages">
            <div class="messages-container pa-4" :style="showFinancingOption ? 'height: calc(100vh - 240px); overflow-y: auto; scroll-behavior: smooth;' : 'height: calc(100vh - 180px); overflow-y: auto; scroll-behavior: smooth;'">
              <!-- Debug info (remove in production) -->
              <div v-if="!hasMessages" class="text-center pa-4 grey--text">
                <div>No messages yet...</div>
                <div class="caption">Messages: {{ messages.length }}</div>
                <div class="caption">Initialized: {{ isInitialized }}</div>
                <div class="caption">Conversation ID: {{ conversationId }}</div>
                <v-btn small color="orange" @click="clearSessionAndRestart" class="mt-2">
                  Clear Session & Restart
                </v-btn>
              </div>

              <!-- Use same structure as popup mode for consistency -->
              <div
                v-for="(message, index) in messages"
                :key="index"
                class="message-wrapper mb-4"
                :class="message.sender_type === 'user' ? 'user-message' : 'assistant-message'"
              >
                <div class="d-flex" :class="message.sender_type === 'user' ? 'justify-end' : 'justify-start'">
                  <v-avatar
                    v-if="message.sender_type === 'assistant'"
                    size="40"
                    class="mr-3 mt-1"
                  >
                    <img :src="userAvatar" :alt="userName" />
                  </v-avatar>

                  <div
                    class="message-bubble pa-3 rounded"
                    :class="{
                      'blue darken-1 white--text': message.sender_type === 'user',
                      'grey lighten-4': message.sender_type === 'assistant'
                    }"
                    style="max-width: 70%;"
                  >
                    <div class="message-text" v-if="message.sender_type === 'assistant'" v-html="formatMessage(message.message)"></div>
                    <div class="message-text" v-else>{{ message.message }}</div>
                    <div class="message-time caption mt-1" :class="message.sender_type === 'user' ? 'white--text' : 'grey--text'">
                      {{ formatTime(message.sent_at) }}
                    </div>
                  </div>

                  <v-avatar
                    v-if="message.sender_type === 'user'"
                    size="40"
                    color="blue"
                    class="ml-3 mt-1"
                  >
                    <v-icon color="white" class="material-icons-round">person</v-icon>
                  </v-avatar>
                </div>
              </div>

              <!-- Typing Indicator -->
              <div v-if="isTyping" class="message-wrapper mb-4 assistant-message">
                <div class="d-flex justify-start">
                  <v-avatar size="40" class="mr-3 mt-1">
                    <img :src="userAvatar" :alt="userName" />
                  </v-avatar>
                  <div class="typing-indicator grey lighten-4 pa-3 rounded">
                    <div class="typing-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Voice Call Transcript (Full Page Mode) -->
              <div v-if="voiceMode === 'voice' && webCallTranscript.length > 0" class="voice-transcript-fullpage mt-4">
                <v-divider class="mb-3"></v-divider>
                <h5 class="text-h6 font-weight-bold mb-3 orange--text">
                  <v-icon class="mr-2 material-icons-round">record_voice_over</v-icon>
                  Live Voice Conversation Transcript
                </h5>
                <div class="transcript-container-fullpage" style="max-height: 200px; overflow-y: auto; background: rgba(255, 255, 255, 0.8); border-radius: 12px; padding: 16px;">
                  <div v-for="(message, index) in webCallTranscript" :key="index" class="transcript-message-fullpage mb-2">
                    <div class="d-flex align-start">
                      <v-chip
                        :color="message.role === 'agent' ? 'orange' : 'blue'"
                        text-color="white"
                        small
                        class="mr-3"
                      >
                        {{ message.role === 'agent' ? 'AI Assistant' : 'You' }}
                      </v-chip>
                      <span class="text-body-2">{{ message.content }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>

          <!-- Voice Mode Toggle (Full Page Mode) -->
          <div v-if="hasVoiceAgent" class="voice-mode-toggle-fullpage pa-3" style="background: rgba(255, 255, 255, 0.95); border-top: 1px solid #e0e0e0;">
            <v-btn-toggle
              v-model="voiceMode"
              mandatory
              class="d-flex justify-center mb-3"
              style="max-width: 300px; margin: 0 auto;"
            >
              <v-btn
                value="chat"
                class="px-6"
                :class="{ 'orange white--text': voiceMode === 'chat' }"
              >
                <v-icon left class="material-icons-round">chat</v-icon>
                Chat Mode
              </v-btn>
              <v-btn
                value="voice"
                class="px-6"
                :class="{ 'orange white--text': voiceMode === 'voice' }"
              >
                <v-icon left class="material-icons-round">mic</v-icon>
                Voice Mode
              </v-btn>
            </v-btn-toggle>
          </div>

          <!-- Full Page Chat Input -->
          <v-card-actions class="chat-input pa-3" style="border-top: 1px solid #e0e0e0; background: rgba(255, 255, 255, 0.98); backdrop-filter: blur(10px);">
            <!-- Voice Mode Controls (Full Page) -->
            <div v-if="voiceMode === 'voice' && hasVoiceAgent" class="voice-controls-fullpage d-flex align-center justify-center" style="width: 100%;">
              <div v-if="!isWebCallActive" class="text-center">
                <v-btn
                  color="green"
                  large
                  dark
                  @click="startWebCall"
                  :loading="webCallLoading"
                  class="px-8 py-3 mb-3"
                  style="border-radius: 25px; font-weight: bold; text-transform: none;"
                >
                  <v-icon left class="material-icons-round">call</v-icon>
                  Start Voice Conversation
                </v-btn>
                <p class="text-body-2 grey--text mb-0">Click to start talking with your AI assistant</p>
              </div>

              <div v-else class="text-center" style="width: 100%;">
                <div class="d-flex justify-center align-center mb-3">
                  <v-chip
                    :color="isAgentSpeaking ? 'orange' : 'blue'"
                    text-color="white"
                    class="mr-3"
                  >
                    <v-icon left small class="material-icons-round">
                      {{ isAgentSpeaking ? 'record_voice_over' : 'mic' }}
                    </v-icon>
                    {{ isAgentSpeaking ? 'AI Assistant Speaking' : 'Listening to You' }}
                  </v-chip>
                  <v-chip color="green" text-color="white">
                    <v-icon left small class="material-icons-round">phone_in_talk</v-icon>
                    Voice Call Active
                  </v-chip>
                </div>

                <v-btn
                  color="red"
                  large
                  dark
                  @click="stopWebCall"
                  class="px-8 py-3"
                  style="border-radius: 25px; font-weight: bold; text-transform: none;"
                >
                  <v-icon left class="material-icons-round">call_end</v-icon>
                  End Voice Call
                </v-btn>
              </div>
            </div>

            <!-- Chat Mode Controls (Full Page) -->
            <div v-else class="chat-controls-fullpage d-flex align-center" style="width: 100%;">
              <v-text-field
                v-model="userInput"
                placeholder="Type your message here..."
                outlined
                dense
                hide-details
                @keyup.enter="sendMessage"
                :disabled="isTyping || (isAgreementRequired && !informationCollectionState.isCollecting)"
                class="mr-3"
                style="border-radius: 25px;"
              >
              </v-text-field>
              <v-btn
                color="orange"
                @click="sendMessage"
                :disabled="!userInput.trim() || isTyping || (isAgreementRequired && !informationCollectionState.isCollecting)"
                class="font-weight-bold"
                elevation="2"
                fab
                small
                :loading="isTyping"
              >
                <v-icon class="material-icons-round">send</v-icon>
              </v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </div>
    </template>

    <!-- Agreement Modal -->
    <v-dialog v-model="agreementModalOpen" persistent>
      <v-card>
        <v-card-title class="headline">
          <span>Pre-Approval Agreement</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeAgreementModal">
            <v-icon class="material-icons-round">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-2">
          <Agreement
            :fromChatModal="true"
            :preFilledData="informationCollectionState.collectedData"
            :refId="userId"
            @agreement-completed="onAgreementCompleted"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dealer Financing Modal -->
    <v-dialog v-model="financingModalOpen" fullscreen persistent>
      <v-card style="height: 100vh; display: flex; flex-direction: column;">
        <v-card-title class="headline flex-shrink-0" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white;">
          <span>Dealer Financing Application</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeFinancingModal" color="white">
            <v-icon class="material-icons-round">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0 flex-grow-1">
          <iframe
            :src="financingUrl"
            width="100%"
            height="100%"
            frameborder="0"
            ref="financingIframe"
            style="border: none;"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Inventory Modal -->
    <v-dialog v-model="inventoryModalOpen" fullscreen persistent>
      <v-card style="height: 100vh; display: flex; flex-direction: column;">
        <v-card-title class="headline flex-shrink-0" style="background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%); color: white;">
          <div class="flex-grow-1">
            <div>Vehicle Inventory</div>
            <div v-if="hasActiveFilters" class="caption" style="opacity: 0.9;">
              {{ getActiveFiltersText }}
            </div>
          </div>
          <v-btn icon @click="closeInventoryModal" color="white">
            <v-icon class="material-icons-round">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0 flex-grow-1">
          <div v-if="inventoryModalOpen" class="inventory-modal-wrapper" @click="handleInventoryClick">
            <!-- Show Vehicle Detail when a specific vehicle is selected -->
            <CardInventoryDetail
              v-if="showVehicleDetail"
              :is-modal-mode="true"
              :vehicle-id="selectedVehicleId"
              @vehicle-viewed="handleVehicleViewed"
              @back-to-list="handleBackToList"
            />

            <!-- Show InventoryFilter when no specific filters are applied -->
            <InventoryFilter
              v-else-if="showInventoryFilter"
              ref="inventoryFilter"
              :is-modal-mode="true"
              @vehicle-viewed="handleVehicleViewed"
              @filter-applied="handleFilterApplied"
            />

            <!-- Show CardInventory when specific filters are applied -->
            <CardInventory
              v-else
              ref="cardInventory"
              :is-modal-mode="true"
              @vehicle-viewed="handleVehicleViewed"
              @vehicle-detail-requested="handleVehicleDetailRequested"
              @agreement-requested="handleAgreementRequested"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Agreement from './Admin/Agreement.vue'
import CardInventory from './CardInventory.vue'
import InventoryFilter from './InventoryFilter.vue'
import CardInventoryDetail from './CardInventoryDetail.vue'

export default {
  name: 'DigitalAssistantChat',
  components: {
    Agreement,
    CardInventory,
    InventoryFilter,
    CardInventoryDetail
  },
  props: {
    userId: {
      type: String,
      required: true
    },
    userName: {
      type: String,
      required: true
    },
    userAvatar: {
      type: String,
      required: true
    },
    domain: {
      type: String,
      required: true
    },
    autoStart: {
      type: Boolean,
      default: true
    },
    fullPage: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showChat: false, // Always show chat in both modes
      messages: [],
      userInput: '',
      isTyping: false,
      conversationId: null,
      sessionId: null,
      isInitialized: false,
      viewedVehicles: [],
      currentVehicleContext: null,
      searchContext: null,
      isOnline: true, // Simple green online indicator
      isAgreementRequired: false, // Track if agreement is required
      showFinancingOption: false, // Show financing form option after agreement
      financingFormOpen: false, // Track if financing form is open
      agreementModalOpen: false, // Track if agreement modal is open
      financingModalOpen: false, // Track if financing modal is open
      financingUrl: 'https://roundtables.mycarsonline.com/apply-online', // Financing form URL
      inventoryModalOpen: false, // Track if inventory modal is open
      originalRouterPush: null, // Store original router push method
      originalRoute: null, // Store original route before opening inventory
      currentInventoryFilters: {}, // Store current inventory filter parameters
      selectedVehicleId: null, // Store selected vehicle ID for detail view
      showVehicleDetail: false, // Track if showing vehicle detail
      // New properties for information collection flow
      informationCollectionState: {
        isCollecting: false,
        currentStep: null, // 'first_name', 'last_name', 'email', 'phone'
        collectedData: {
          first_name: '',
          last_name: '',
          email: '',
          phone: ''
        },
        isComplete: false
      },
      preQualificationState: {
        isAsking: false,
        hasCompleted: false
      },
      // Voice Agent Properties
      voiceMode: 'chat', // 'chat' or 'voice'
      hasVoiceAgent: false, // Will be set based on user's voice agent status
      webCallLoading: false,
      micTestLoading: false,
      isWebCallActive: false,
      isAgentSpeaking: false,
      webCallTranscript: [],
      retellWebClient: null,
      voiceAgentData: null // Store voice agent configuration
    }
  },
  computed: {
    hasMessages() {
      return this.messages && this.messages.length > 0;
    },

    hasActiveFilters() {
      return Object.keys(this.currentInventoryFilters).length > 0;
    },

    getActiveFiltersText() {
      const filters = [];
      const params = this.currentInventoryFilters;

      if (params.body_style) filters.push(`Body: ${params.body_style}`);
      if (params.make) filters.push(`Make: ${params.make}`);
      if (params.model) filters.push(`Model: ${params.model}`);
      if (params.year) filters.push(`Year: ${params.year}`);
      if (params.price_min || params.price_max) {
        const priceRange = `$${params.price_min || '0'} - $${params.price_max || '∞'}`;
        filters.push(`Price: ${priceRange}`);
      }

      return filters.length > 0 ? `Filtered by: ${filters.join(', ')}` : '';
    },

    // Determine whether to show InventoryFilter or CardInventory
    showInventoryFilter() {
      // Don't show filter if we're showing vehicle detail
      if (this.showVehicleDetail) {
        return false;
      }

      // Show InventoryFilter when no specific filters are applied
      // or when only default/empty filters are present
      const params = this.currentInventoryFilters;

      console.log('🔧 showInventoryFilter - params:', params);

      if (!params || Object.keys(params).length === 0) {
        console.log('🔧 showInventoryFilter - no params, showing filter');
        return true; // No filters at all
      }

      // Check if any meaningful filters are applied
      const hasSpecificFilters =
        (params.body_style && params.body_style !== '') ||
        (params.make && params.make !== '') ||
        (params.model && params.model !== '') ||
        (params.year && params.year !== '') ||
        (params.price_min && params.price_min !== '') ||
        (params.price_max && params.price_max !== '') ||
        (params.search_query && params.search_query !== '');

      console.log('🔧 showInventoryFilter - hasSpecificFilters:', hasSpecificFilters, 'returning:', !hasSpecificFilters);
      return !hasSpecificFilters; // Show filter page if no specific filters
    }
  },
  watch: {
    messages: {
      handler(newMessages) {
        console.log('📝 Messages updated:', newMessages.length, newMessages);
        if (newMessages.length > 0) {
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      },
      deep: true
    }
  },
  mounted() {
    console.log('🎯 DigitalAssistantChat mounted', {
      fullPage: this.fullPage,
      autoStart: this.autoStart,
      userId: this.userId,
      domain: this.domain
    });

    // Prevent multiple instances in full-page mode
    if (this.fullPage) {
      const existingFullPageChats = document.querySelectorAll('.full-page-mode')
      if (existingFullPageChats.length > 1) {
        console.warn('⚠️ Multiple full-page chat instances detected, destroying this instance')
        this.$destroy()
        return
      }
    }

    // Store original route for restoration when closing inventory modal
    this.originalRoute = this.$route.path;

    // Set up global agreement modal function
    window.openAgreementModal = this.openAgreementModal;

    // Set up global inventory modal functions
    window.openInventoryModal = this.openInventoryModal;
    window.openInventoryWithFilters = this.openInventoryWithFilters;
    window.openInventoryByPriceRange = this.openInventoryByPriceRange;
    window.openInventoryByBodyStyle = this.openInventoryByBodyStyle;
    window.openInventoryShowAll = this.openInventoryShowAll;

    // Check localStorage for agreement completion
    this.checkLocalStorageAgreementStatus();

    // Load voice agent data
    this.loadVoiceAgentData();

    // Load existing session or create new one
    this.loadOrCreateSession();

    // Clean up message format (only if messages exist)
    if (this.messages && this.messages.length > 0) {
      console.log('🔧 Normalizing messages:', this.messages.length);
      this.normalizeMessages();
    }

    if ((this.autoStart && this.showChat) || this.fullPage ) {
      console.log('⏰ Setting up auto-start timer...');
      // Auto-start conversation after a short delay
      setTimeout(() => {
        console.log('🚀 Auto-starting conversation...');
        this.initializeConversation()
      }, this.fullPage ? 1000 : 2000) // Faster start for full-page mode
    }
  },

  beforeDestroy() {
    // Restore router if it was overridden
    if (this.originalRouterPush) {
      this.$router.push = this.originalRouterPush;
      this.originalRouterPush = null;
    }

    // Clean up web call
    if (this.retellWebClient && this.isWebCallActive) {
      this.retellWebClient.stopCall();
    }
  },

  methods: {
    // Load existing session or create new one
    loadOrCreateSession() {
      console.log('🔄 Loading or creating session for user:', this.userId);
      const sessionKey = `digital_assistant_session_${this.userId}`;
      const existingSession = localStorage.getItem(sessionKey);

      if (existingSession) {
        try {
          const sessionData = JSON.parse(existingSession);
          this.sessionId = sessionData.sessionId;
          this.conversationId = sessionData.conversationId;
          this.messages = sessionData.messages || [];
          this.viewedVehicles = sessionData.viewedVehicles || [];
          this.currentVehicleContext = sessionData.currentVehicleContext || null;
          this.searchContext = sessionData.searchContext || null;
          this.isInitialized = sessionData.isInitialized || false;
          this.isAgreementRequired = sessionData.isAgreementRequired || false;
          this.showFinancingOption = sessionData.showFinancingOption || false;
          this.informationCollectionState = sessionData.informationCollectionState || {
            isCollecting: false,
            currentStep: null,
            collectedData: { first_name: '', last_name: '', email: '', phone: '' },
            isComplete: false
          };
          this.preQualificationState = sessionData.preQualificationState || {
            isAsking: false,
            hasCompleted: false
          };

          console.log('✅ Loaded existing session:', {
            sessionId: this.sessionId,
            messagesCount: this.messages.length,
            isInitialized: this.isInitialized
          });

          // If we have messages and conversation is initialized, emit loaded event
          if (this.isInitialized && this.messages.length > 0) {
            this.$emit('conversation-loaded')
          }
        } catch (error) {
          console.error('❌ Error loading session:', error);
          this.createNewSession();
        }
      } else {
        console.log('🆕 No existing session found, creating new one');
        this.createNewSession();
      }
    },

    // Create new session
    createNewSession() {
      this.sessionId = this.generateSessionId();
      this.conversationId = null;
      this.messages = [];
      this.viewedVehicles = [];
      this.isInitialized = false;

      console.log('🆕 Created new session:', {
        sessionId: this.sessionId,
        userId: this.userId,
        domain: this.domain
      });
      this.saveSession();
    },

    // Generate unique session ID
    generateSessionId() {
      return 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
    },

    // Save session to localStorage
    saveSession() {
      const sessionKey = `digital_assistant_session_${this.userId}`;
      const sessionData = {
        sessionId: this.sessionId,
        conversationId: this.conversationId,
        messages: this.messages,
        viewedVehicles: this.viewedVehicles,
        currentVehicleContext: this.currentVehicleContext,
        searchContext: this.searchContext,
        isInitialized: this.isInitialized,
        isAgreementRequired: this.isAgreementRequired,
        showFinancingOption: this.showFinancingOption,
        informationCollectionState: this.informationCollectionState,
        preQualificationState: this.preQualificationState,
        lastUpdated: new Date().toISOString()
      };

      localStorage.setItem(sessionKey, JSON.stringify(sessionData));
    },

    async initializeConversation() {
      console.log('🚀 Initializing conversation...', {
        isInitialized: this.isInitialized,
        messagesLength: this.messages.length,
        domain: this.domain,
        userId: this.userId,
        sessionId: this.sessionId
      });

      if (this.isInitialized) {
        console.log('✅ Already initialized, showing existing chat');
        // If already initialized and has messages, just show the chat
        if (this.messages.length > 0) {
          this.showChat = true;
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
        return;
      }

      try {
        console.log('📡 Starting conversation API call...');
        const response = await axios.post('/api/digital-assistant/start-conversation', {
          domain: this.domain,
          user_id: this.userId,
          session_id: this.sessionId,
          agreement_completed_localStorage: localStorage.getItem('agreement_completed')
        })

        console.log('📨 API Response:', response.data);

        if (response.data.success) {
          this.conversationId = response.data.conversation_id
          this.isInitialized = true

          // Handle information collection state from initial response
          if (response.data.information_collection_state) {
            this.informationCollectionState = response.data.information_collection_state;
          }

          // Check if agreement is required based on response flags
          const initialMessage = response.data.message || 'Hello! How can I help you today?';

          // For new information collection flow, don't require agreement initially
          if (response.data.agreement_required === false || initialMessage.includes('first name')) {
            this.isAgreementRequired = false;
            this.showFinancingOption = false; // Don't show financing until after agreement
          } else if (initialMessage.includes('agreement') || initialMessage.includes('pre-approved')) {
            this.isAgreementRequired = true;
            this.showFinancingOption = false;
          } else {
            this.isAgreementRequired = false;
            this.showFinancingOption = true;
          }

          // Add initial message only if no messages exist
          if (this.messages.length === 0) {
            this.messages.push({
              sender_type: 'assistant',
              message: initialMessage,
              sent_at: new Date().toISOString()
            })
            console.log('💬 Added initial message');
          }

          // Auto-show chat with initial message
          this.showChat = true
          this.$nextTick(() => {
            this.scrollToBottom()
          })

          // Save session after initialization
          this.saveSession()
          console.log('✅ Conversation initialized successfully');

          // Emit success event
          this.$emit('conversation-loaded')
        } else {
          console.warn('⚠️ API returned success: false', response.data);
          this.$emit('conversation-error', 'Failed to initialize conversation')
        }
      } catch (error) {
        console.error('❌ Failed to initialize conversation:', error)
        this.$emit('conversation-error', error.message || 'Failed to initialize conversation')

        // Add fallback message if API fails
        if (this.messages.length === 0) {
          this.messages.push({
            sender_type: 'assistant',
            message: 'Hello! How can I help you today?',
            sent_at: new Date().toISOString()
          })
          this.showChat = true
          this.$nextTick(() => {
            this.scrollToBottom()
          })
        }
      }
    },

    toggleChat() {
      // In full-page mode, don't allow hiding the chat
      if (this.fullPage) {
        this.showChat = true
      } else {
        this.showChat = !this.showChat
      }

      if (this.showChat && !this.isInitialized) {
        this.initializeConversation()
      }

      if (this.showChat) {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },

    async sendMessage() {
      if (!this.userInput.trim() || this.isTyping) {
        console.log('⚠️ Message blocked:', {
          userInput: this.userInput.trim(),
          isTyping: this.isTyping
        });
        return;
      }

      const message = this.userInput.trim()
      this.userInput = ''

      console.log('📤 Sending message:', message);

      // Add user message
      this.messages.push({
        sender_type: 'user',
        message: message,
        sent_at: new Date()
      })

      this.scrollToBottom()
      this.isTyping = true

      try {
        // Debug: Log what we're sending
        console.log('🤖 Sending message to AI:', {
          conversation_id: this.conversationId,
          message: message,
          current_vehicle_context: this.currentVehicleContext,
          search_context: this.searchContext,
          viewed_vehicles: this.viewedVehicles,
          information_collection_state: this.informationCollectionState,
          pre_qualification_state: this.preQualificationState
        });

        const response = await axios.post('/api/digital-assistant/send-message', {
          conversation_id: this.conversationId,
          message: message,
          domain: this.domain,
          current_vehicle_context: this.currentVehicleContext,
          search_context: this.searchContext,
          viewed_vehicles: this.viewedVehicles,
          agreement_completed_localStorage: localStorage.getItem('agreement_completed'),
          information_collection_state: this.informationCollectionState,
          pre_qualification_state: this.preQualificationState
        })

        if (response.data.success) {
          console.log('📥 Received response:', response.data);

          // Handle information collection state updates
          if (response.data.information_collection_state) {
            this.informationCollectionState = response.data.information_collection_state;
          }

          // Handle pre-qualification state updates
          if (response.data.pre_qualification_state) {
            this.preQualificationState = response.data.pre_qualification_state;
          }

          // Check if information collection is complete and should show agreement
          if (response.data.show_agreement_modal && this.informationCollectionState.isComplete) {
            this.openAgreementModalWithData();
            return; // Don't add the response message, let the modal handle it
          }

          // Check if should show pre-qualification button
          if (response.data.show_prequalification_button) {
            this.showFinancingOption = true;
          }

          // Check if should show inventory suggestion (after user says Yes to pre-qualification)
          if (response.data.show_inventory_suggestion) {
            // Add inventory suggestion after the response message
            setTimeout(() => {
              this.addInventorySuggestionMessage();
            }, 1500);
          }

          // Check if agreement is required
          if (response.data.agreement_required) {
            this.isAgreementRequired = true;
            this.showFinancingOption = false;
          } else {
            this.isAgreementRequired = false;
            if (!this.informationCollectionState.isCollecting && !this.preQualificationState.isAsking) {
              this.showFinancingOption = true; // Show financing option for post-agreement users
            }
          }

          // Add assistant response
          this.messages.push({
            sender_type: 'assistant',
            message: response.data.response,
            sent_at: new Date()
          })
        }
      } catch (error) {
        console.error('Failed to send message:', error)
        // Add error message
        this.messages.push({
          sender_type: 'assistant',
          message: "I'm sorry, I'm having trouble responding right now. Please try again.",
          sent_at: new Date()
        })
      } finally {
        this.isTyping = false
        this.scrollToBottom()
        // Save session after each message
        this.saveSession()
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const messagesContainer = this.$refs.chatMessages?.$el || this.$refs.chatMessages
        if (messagesContainer) {
          const scrollElement = messagesContainer.querySelector('.messages-container')
          if (scrollElement) {
            scrollElement.scrollTop = scrollElement.scrollHeight
          }
        }
      })
    },

    normalizeMessages() {
      // Ensure all messages have consistent format
      this.messages = this.messages.map(message => {
        // Ensure sent_at is a valid date
        if (!message.sent_at || isNaN(new Date(message.sent_at).getTime())) {
          message.sent_at = new Date().toISOString()
        }

        // Ensure message has proper sender_type
        if (!message.sender_type) {
          message.sender_type = message.type === 'ai' ? 'assistant' : 'user'
        }

        return message
      })
    },

    formatTime(date) {
      if (!date) return ''

      try {
        const dateObj = new Date(date)
        if (isNaN(dateObj.getTime())) {
          return ''
        }
        return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      } catch (error) {
        console.warn('Invalid date format:', date)
        return ''
      }
    },

    // Method to track vehicle views (called from parent component)
    trackVehicleView(vehicleId, vehicleData) {
      // Add to viewed vehicles array
      const existingIndex = this.viewedVehicles.findIndex(v => v.vehicle_id === vehicleId);
      const vehicleViewData = {
        vehicle_id: vehicleId,
        vehicle_data: vehicleData,
        viewed_at: new Date().toISOString()
      };

      if (existingIndex >= 0) {
        // Update existing entry
        this.viewedVehicles[existingIndex] = vehicleViewData;
      } else {
        // Add new entry
        this.viewedVehicles.push(vehicleViewData);
      }

      // Save session with updated vehicle data
      this.saveSession();

      // Track with API if conversation exists
      if (this.conversationId) {
        axios.post('/api/digital-assistant/track-vehicle-view', {
          conversation_id: this.conversationId,
          vehicle_id: vehicleId,
          vehicle_data: vehicleData
        }).catch(error => {
          console.error('Failed to track vehicle view:', error)
        })
      }

      console.log('Vehicle tracked:', vehicleId, vehicleData);
    },

    // Method to set current vehicle context (called from parent components)
    setCurrentVehicleContext(vehicleData) {
      this.currentVehicleContext = vehicleData;
      console.log('🚗 Current vehicle context set:', vehicleData);

      // Save to session
      this.saveSession();
    },

    // Method to get current vehicle context
    getCurrentVehicleContext() {
      return this.currentVehicleContext;
    },

    // Format message for display (handle HTML content)
    formatMessage(message) {
      if (!message) return '';

      // Escape HTML to prevent XSS, but we'll add our own links
      let formattedMessage = message;

      // First, handle agreement links specifically (before general URL processing)
      const agreementRegex = /(https?:\/\/[^\s]*agreement[^\s]*)/gi;
      formattedMessage = formattedMessage.replace(agreementRegex, '<a href="javascript:void(0)" onclick="window.openAgreementModal(\'$1\')" class="agreement-link">🔗 Click here to complete your agreement</a>');

      // Handle inventory suggestions
      if (formattedMessage.includes("would you like to start looking at our inventory")) {
        formattedMessage += '<br><br><a href="javascript:void(0)" onclick="window.openInventoryModal()" class="inventory-link" style="background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%); color: white; padding: 8px 16px; border-radius: 6px; text-decoration: none; display: inline-block; margin-top: 8px;"><span style="margin-right: 8px;">🚗</span>View Our Inventory</a>';
      }

      // Handle various inventory-related requests
      const inventoryPatterns = [
        {
          pattern: /show me (cars|vehicles|inventory)/i,
          button: '<br><br><a href="javascript:void(0)" onclick="window.openInventoryShowAll()" class="inventory-link" style="background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%); color: white; padding: 8px 16px; border-radius: 6px; text-decoration: none; display: inline-block; margin-top: 8px;"><span style="margin-right: 8px;">🚗</span>View All Vehicles</a>'
        },
        {
          pattern: /under \$?(\d+)/i,
          button: (_, price) => `<br><br><a href="javascript:void(0)" onclick="window.openInventoryByPriceRange('', '${price}')" class="inventory-link" style="background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%); color: white; padding: 8px 16px; border-radius: 6px; text-decoration: none; display: inline-block; margin-top: 8px;"><span style="margin-right: 8px;">💰</span>Cars Under $${price}</a>`
        },
        {
          pattern: /between \$?(\d+) and \$?(\d+)/i,
          button: (_, min, max) => `<br><br><a href="javascript:void(0)" onclick="window.openInventoryByPriceRange('${min}', '${max}')" class="inventory-link" style="background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%); color: white; padding: 8px 16px; border-radius: 6px; text-decoration: none; display: inline-block; margin-top: 8px;"><span style="margin-right: 8px;">💰</span>Cars $${min} - $${max}</a>`
        },
        {
          pattern: /(suv|truck|sedan|coupe|convertible|hatchback|wagon)/i,
          button: (_, bodyStyle) => `<br><br><a href="javascript:void(0)" onclick="window.openInventoryByBodyStyle('${bodyStyle.charAt(0).toUpperCase() + bodyStyle.slice(1)}')" class="inventory-link" style="background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%); color: white; padding: 8px 16px; border-radius: 6px; text-decoration: none; display: inline-block; margin-top: 8px;"><span style="margin-right: 8px;">🚙</span>${bodyStyle.toUpperCase()}s</a>`
        },
        {
          pattern: /(toyota|honda|ford|chevrolet|nissan|bmw|mercedes|audi|lexus|hyundai|kia|mazda|subaru|volkswagen|jeep|ram|gmc|cadillac|lincoln|acura|infiniti|volvo|porsche|jaguar|land rover|mini|fiat|alfa romeo|genesis|mitsubishi|buick|chrysler)/i,
          button: (_, make) => `<br><br><a href="javascript:void(0)" onclick="window.openInventoryWithFilters('${make.charAt(0).toUpperCase() + make.slice(1)}', '', '', '', '', '')" class="inventory-link" style="background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%); color: white; padding: 8px 16px; border-radius: 6px; text-decoration: none; display: inline-block; margin-top: 8px;"><span style="margin-right: 8px;">🏷️</span>${make.charAt(0).toUpperCase() + make.slice(1)} Vehicles</a>`
        }
      ];

      // Apply inventory patterns
      inventoryPatterns.forEach(({ pattern, button }) => {
        const match = formattedMessage.match(pattern);
        if (match && !formattedMessage.includes('inventory-link')) { // Avoid duplicate buttons
          if (typeof button === 'function') {
            formattedMessage += button(...match);
          } else {
            formattedMessage += button;
          }
        }
      });

      // Then handle other URLs that are not already processed
      const urlRegex = /(https?:\/\/[^\s<]+)(?![^<]*<\/a>)/g;
      formattedMessage = formattedMessage.replace(urlRegex, (match) => {
        // Skip if this URL is already processed as an agreement link
        if (formattedMessage.includes(`onclick="window.openAgreementModal('${match}')`)) {
          return match;
        }
        return `<a href="${match}" target="_blank" rel="noopener noreferrer" class="regular-link">${match}</a>`;
      });

      return formattedMessage;
    },

    // Check localStorage for agreement completion status
    checkLocalStorageAgreementStatus() {
      const agreementCompleted = localStorage.getItem('agreement_completed');
      const agreementEmail = localStorage.getItem('agreement_user_email');

      if (agreementCompleted === 'true' && agreementEmail) {
        console.log('✅ Agreement completed found in localStorage');
        this.isAgreementRequired = false;
        this.showFinancingOption = true;
      }
    },

    // Open agreement form in modal
    openAgreementModal() {
      console.log('🔗 Opening agreement modal');
      this.agreementModalOpen = true;
    },

    // Open agreement modal with pre-filled data from information collection
    openAgreementModalWithData() {
      console.log('🔗 Opening agreement modal with pre-filled data:', this.informationCollectionState.collectedData);
      this.agreementModalOpen = true;
    },

    // Close agreement modal
    closeAgreementModal() {
      this.agreementModalOpen = false;
    },

    // Handle agreement completion event from Agreement component
    onAgreementCompleted(data) {
      console.log('✅ Agreement completed:', data);

      // Update component state
      this.isAgreementRequired = false;
      this.informationCollectionState.isComplete = true;
      this.preQualificationState.isAsking = true;

      // Close modal
      this.closeAgreementModal();

      // Add the new post-agreement message asking about pre-qualification
      setTimeout(() => {
        this.addPreQualificationCheckMessage();
      }, 1000);
    },

    // Open financing form in modal
    openFinancingModal() {
      console.log('💳 Opening financing modal');
      this.financingModalOpen = true;
    },

    // Close financing modal
    closeFinancingModal() {
      this.financingModalOpen = false;
    },

    // Add pre-qualification check message after agreement completion
    addPreQualificationCheckMessage() {
      const checkMessage = "Hey, quick check-in — were you able to finish the pre-qual form? Just type Yes if you're all done, or No if you're still working on it. No rush!";

      // Add assistant message with pre-qualification check
      this.messages.push({
        sender_type: 'assistant',
        message: checkMessage,
        sent_at: new Date()
      });

      // Show the pre-qualification button
      this.showFinancingOption = true;

      // Save session with new message
      this.saveSession();

      // Scroll to bottom
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    // Add inventory suggestion message after agreement completion
    addInventorySuggestionMessage() {
      const suggestionMessage = "Great! Your pre-approval is complete. While you're waiting, would you like to start looking at our inventory? I can show you all available vehicles right here in the chat.";

      // Add assistant message with inventory suggestion
      this.messages.push({
        sender_type: 'assistant',
        message: suggestionMessage,
        sent_at: new Date()
      });

      // Save session with new message
      this.saveSession();

      // Scroll to bottom
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    // Open inventory modal with optional filters
    openInventoryModal(filters = null) {
      console.log('🚗 Opening inventory modal with filters:', filters);

      // Set parameters for inventory view - use provided filters or defaults
      this.currentInventoryFilters = filters || {
        make: '',
        model: '',
        year: '',
        price_min: '',
        price_max: '',
        zip_code: '10001',
        radius: '50',
        body_style: '',
        credit_score: '641',
        bhph: '0'
      };

      this.inventoryModalOpen = true;

      // Store original router push method and override it
      this.originalRouterPush = this.$router.push;
      this.$router.push = this.preventNavigation;

      // Initialize CardInventory with the filters
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.cardInventory) {
            console.log('🚗 Initializing CardInventory with parameters:', this.currentInventoryFilters);

            // Get the domain (subdomain) for CardInventory
            const domain = this.getSubdomain();

            // Call CardInventory's getInventory method with custom query parameters
            if (this.$refs.cardInventory.getInventory) {
              this.$refs.cardInventory.getInventory(domain, this.currentInventoryFilters);
            }
          }
        }, 100); // Small delay to ensure component is fully mounted
      });

      // Wait for modal to open, then initialize CardInventory with parameters
      // this.$nextTick(() => {
      //   setTimeout(() => {
      //     if (this.$refs.cardInventory) {
      //       console.log('🚗 Initializing CardInventory with parameters:', this.currentInventoryFilters);

      //       // Get the domain (subdomain) for CardInventory
      //       const domain = this.getSubdomain();

      //       // Call CardInventory's getInventory method with custom query parameters
      //       if (this.$refs.cardInventory.getInventory) {
      //         this.$refs.cardInventory.getInventory(domain, this.currentInventoryFilters);
      //       }
      //     }
      //   }, 100); // Small delay to ensure component is fully mounted
      // });
    },

    // Close inventory modal
    closeInventoryModal() {
      this.inventoryModalOpen = false;

      // Clear current filters
      this.currentInventoryFilters = {};

      // Reset vehicle detail state
      this.showVehicleDetail = false;
      this.selectedVehicleId = null;

      // Restore original router push method
      if (this.originalRouterPush) {
        this.$router.push = this.originalRouterPush;
        this.originalRouterPush = null;
      }
    },

    // Prevent navigation when inventory modal is open
    preventNavigation(location) {
      console.log('� Navigation attempt while inventory modal is open:', location);

      // Handle inventory filtering within the modal
      if (typeof location === 'string' && location.includes('/view-inventory')) {
        console.log('📋 Inventory filter applied:', location);

        // Extract URL parameters and update the inventory component
        this.updateInventoryWithFilters(location);

        // Prevent actual navigation but return resolved promise
        return Promise.resolve();
      }

      // Prevent navigation to other pages
      if (typeof location === 'string') {
        if (location.includes('/vehicle-detail') ||
            location.includes('/login') ||
            location.includes('/signup') ||
            location.includes('/reviews') ||
            location.includes('/card-payment')) {
          console.log('🚫 Navigation prevented:', location);
          return Promise.resolve();
        }
      }

      // For other navigation attempts, prevent them
      console.log('🚫 General navigation prevented:', location);
      return Promise.resolve();
    },

    // Update inventory component with filter parameters
    updateInventoryWithFilters(url) {
      try {
        // Parse URL parameters
        const urlParts = url.split('?');
        if (urlParts.length > 1) {
          const params = new URLSearchParams(urlParts[1]);
          const filterParams = {};

          // Extract all parameters
          for (let [key, value] of params.entries()) {
            if (value && value !== '') {
              filterParams[key] = value;
            }
          }

          console.log('🔧 Applying filters to inventory:', filterParams);

          // Store the current filter parameters
          this.currentInventoryFilters = filterParams;

          // Trigger inventory update by emitting event or calling method
          this.$nextTick(() => {
            if (this.$refs.inventoryFilter) {
              // Try to update the inventory component directly
              this.applyFiltersToInventory(filterParams);
            }
          });
        }
      } catch (error) {
        console.error('Error parsing inventory filters:', error);
      }
    },

    // Apply filters to the inventory component
    applyFiltersToInventory(filterParams) {
      // This method will apply the filters to the InventoryFilter component
      // We need to access the component's data and methods
      const inventoryComponent = this.$refs.inventoryFilter;

      if (inventoryComponent) {
        // Update the filter object in the inventory component
        if (inventoryComponent.filter) {
          Object.assign(inventoryComponent.filter, {
            make: filterParams.make || '',
            model: filterParams.model || '',
            year: filterParams.year || '',
            price_min: filterParams.price_min || '',
            price_max: filterParams.price_max || '',
            zip_code: filterParams.zip_code || '',
            radius: filterParams.radius || 50,
            body_style: filterParams.body_style || '',
            credit_score: filterParams.credit_score || '',
            bhph: filterParams.bhph || 0
          });

          console.log('✅ Filters applied to inventory component');

          // Trigger a search/update in the inventory component if it has such method
          if (typeof inventoryComponent.getInventory === 'function') {
            inventoryComponent.getInventory(inventoryComponent.getSubdomain());
          }
        }
      }
    },

    // Handle clicks within inventory modal (backup method)
    handleInventoryClick() {
      // Additional click handling if needed
      console.log('Inventory modal clicked');
    },

    // Open financing form in popup iframe
    openFinancingForm() {
      const financingUrl = 'https://roundtables.mycarsonline.com/apply-online';

      // Create popup iframe
      const popup = window.open('', 'financingForm', 'width=800,height=600,scrollbars=yes,resizable=yes');

      if (popup) {
        popup.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Dealer Financing Application</title>
            <style>
              body { margin: 0; padding: 0; }
              iframe { width: 100%; height: 100vh; border: none; }
            </style>
          </head>
          <body>
            <iframe src="${financingUrl}" frameborder="0"></iframe>
          </body>
          </html>
        `);
        popup.document.close();
        this.financingFormOpen = true;
      } else {
        // Fallback: open in new tab if popup is blocked
        window.open(financingUrl, '_blank');
      }
    },

    // Clear session and restart conversation (for debugging)
    clearSessionAndRestart() {
      console.log('🗑️ Clearing session and restarting conversation');
      const sessionKey = `digital_assistant_session_${this.userId}`;
      localStorage.removeItem(sessionKey);

      // Reset all data
      this.messages = [];
      this.conversationId = null;
      this.sessionId = null;
      this.isInitialized = false;
      this.viewedVehicles = [];
      this.currentVehicleContext = null;
      this.searchContext = null;
      this.isAgreementRequired = false;
      this.showFinancingOption = false;
      this.agreementModalOpen = false;
      this.financingModalOpen = false;
      this.inventoryModalOpen = false;
      this.informationCollectionState = {
        isCollecting: false,
        currentStep: null,
        collectedData: { first_name: '', last_name: '', email: '', phone: '' },
        isComplete: false
      };
      this.preQualificationState = {
        isAsking: false,
        hasCompleted: false
      };

      // Restore router if it was overridden
      if (this.originalRouterPush) {
        this.$router.push = this.originalRouterPush;
        this.originalRouterPush = null;
      }

      // Create new session and restart
      this.createNewSession();
      this.initializeConversation();
    },

    // Get subdomain from current URL
    getSubdomain() {
      const hostname = window.location.hostname;
      const domainParts = hostname.split('.');

      // Assuming the domain is of the form subdomain.example.com
      if (domainParts.length > 2) {
        return domainParts[0];
      } else {
        return null; // No subdomain
      }
    },

    // Open inventory with specific make/model filters
    openInventoryWithFilters(make = '', model = '', year = '', priceMin = '', priceMax = '', bodyStyle = '') {
      const filters = {
        make: make,
        model: model,
        year: year,
        price_min: priceMin,
        price_max: priceMax,
        zip_code: '10001',
        radius: '50',
        body_style: bodyStyle,
        credit_score: '641',
        bhph: '0'
      };

      console.log('🚗 Opening inventory with specific filters:', filters);
      this.openInventoryModal(filters);
    },

    // Open inventory for specific price range
    openInventoryByPriceRange(minPrice, maxPrice) {
      const filters = {
        make: '',
        model: '',
        year: '',
        price_min: minPrice.toString(),
        price_max: maxPrice.toString(),
        zip_code: '10001',
        radius: '50',
        body_style: '',
        credit_score: '641',
        bhph: '0'
      };

      console.log('🚗 Opening inventory with price range:', filters);
      this.openInventoryModal(filters);
    },

    // Open inventory for specific vehicle type/body style
    openInventoryByBodyStyle(bodyStyle) {
      const filters = {
        make: '',
        model: '',
        year: '',
        price_min: '',
        price_max: '',
        zip_code: '10001',
        radius: '50',
        body_style: bodyStyle,
        credit_score: '641',
        bhph: '0'
      };

      console.log('🚗 Opening inventory with body style:', filters);
      this.openInventoryModal(filters);
    },

    // Open inventory with no filters (show all)
    openInventoryShowAll() {
      const filters = {
        make: '',
        model: '',
        year: '',
        price_min: '',
        price_max: '',
        zip_code: '10001',
        radius: '50',
        body_style: '',
        credit_score: '641',
        bhph: '0'
      };

      console.log('🚗 Opening inventory with no filters (show all)');
      this.openInventoryModal(filters);
    },

    // Handle vehicle detail requests from modal
    handleVehicleDetailRequested(eventData) {
      console.log('🚗 Vehicle detail requested in modal:', eventData);

      // Set the selected vehicle ID and show vehicle detail
      this.selectedVehicleId = eventData.vehicle_id;
      this.showVehicleDetail = true;

      console.log('🚗 Switching to vehicle detail view for vehicle ID:', this.selectedVehicleId);
    },

    // Handle agreement requests from modal
    handleAgreementRequested() {
      console.log('🚗 Agreement requested from modal');

      // Close inventory modal and open agreement modal
      this.inventoryModalOpen = false;
      this.agreementModalOpen = true;
    },

    // Handle filter applied from InventoryFilter component
    handleFilterApplied(filters) {
      console.log('🔧 Filters applied from InventoryFilter:', filters);
      console.log('🔧 Current filters before update:', this.currentInventoryFilters);

      // Update current filters - this will cause showInventoryFilter to return false
      // and switch to CardInventory component
      this.currentInventoryFilters = {
        ...this.currentInventoryFilters,
        ...filters
      };

      console.log('🔧 Current filters after update:', this.currentInventoryFilters);
      console.log('🔧 showInventoryFilter will be:', this.showInventoryFilter);

      // Initialize CardInventory with the new filters
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.cardInventory) {
            console.log('🚗 Initializing CardInventory with applied filters:', this.currentInventoryFilters);

            // Get the domain (subdomain) for CardInventory
            const domain = this.getSubdomain();

            // Call CardInventory's getInventory method with the applied filters
            if (this.$refs.cardInventory.getInventory) {
              this.$refs.cardInventory.getInventory(domain, this.currentInventoryFilters);
            }
          }
        }, 100); // Small delay to ensure component is fully mounted
      });
    },

    // Handle back to list from vehicle detail
    handleBackToList() {
      console.log('🚗 Back to list requested from vehicle detail');

      // Hide vehicle detail and go back to inventory list
      this.showVehicleDetail = false;
      this.selectedVehicleId = null;
    },

    // Handle contact dealer from vehicle detail
    handleContactDealer() {
      console.log('🚗 Contact dealer requested from vehicle detail');

      // Add a message to the chat
      this.addMessage({
        text: "I'd like to help you contact the dealer about this vehicle. Let me connect you with our sales team.",
        isUser: false,
        timestamp: new Date()
      });

      // You could also close the modal and show contact information
      // or open a contact form
    },

    // Handle get pre-approved from vehicle detail
    handleGetPreApproved() {
      console.log('🚗 Get pre-approved requested from vehicle detail');

      // Close inventory modal and open agreement modal
      this.inventoryModalOpen = false;
      this.showVehicleDetail = false;
      this.selectedVehicleId = null;
      this.agreementModalOpen = true;
    },

    // Voice Agent Methods
    async loadVoiceAgentData() {
      try {
        // Get user_id from domain name (subdomain)
        const subdomain = this.getSubdomain();
        if (!subdomain) {
          console.log('🎤 No subdomain found, voice agent not available');
          return;
        }

        console.log('🎤 Loading voice agent data for subdomain:', subdomain);

        // Call API to get voice agent data for this user/domain
        const response = await axios.get(`/api/client/voice-agent/check-availability/${subdomain}`);

        if (response.data.flag === 1 && response.data.data) {
          this.voiceAgentData = response.data.data;
          this.hasVoiceAgent = response.data.data.has_voice_agent && response.data.data.voice_agent_enabled;

          console.log('🎤 Voice agent data loaded:', {
            hasVoiceAgent: this.hasVoiceAgent,
            agentId: this.voiceAgentData.retell_agent_id
          });
        } else {
          console.log('🎤 Voice agent not available for this user');
          this.hasVoiceAgent = false;
        }
      } catch (error) {
        console.error('❌ Error loading voice agent data:', error);
        this.hasVoiceAgent = false;
      }
    },

    async startWebCall() {
      console.log('🎤 Starting web call...', {
        hasVoiceAgent: this.hasVoiceAgent,
        voiceAgentData: this.voiceAgentData
      });

      if (!this.hasVoiceAgent || !this.voiceAgentData) {
        this.$toast.error('Voice agent is not available');
        return;
      }

      // Check if we're on HTTPS (required for microphone access)
      if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
        this.$toast.error('Voice calls require HTTPS. Please use a secure connection.');
        return;
      }

      this.webCallLoading = true;
      try {
        // Check microphone permissions first
        console.log('🎤 Checking microphone permissions...');
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach(track => track.stop()); // Stop the test stream
          console.log('✅ Microphone access granted');
        } catch (permissionError) {
          console.error('❌ Microphone permission denied:', permissionError);
          this.$toast.error('Microphone access is required for voice calls. Please allow microphone access and try again.');
          return;
        }

        // Import Retell Web SDK dynamically
        if (!this.retellWebClient) {
          try {
            console.log('🎤 Loading Retell Web SDK...');
            const { RetellWebClient } = await import('retell-client-js-sdk');
            this.retellWebClient = new RetellWebClient();
            this.setupWebCallEventListeners();
            console.log('✅ Retell Web SDK loaded successfully');
          } catch (importError) {
            console.error('❌ Failed to import Retell Web SDK:', importError);
            this.$toast.error('Voice SDK not available. Please install the required dependencies.');
            return;
          }
        }

        // Call backend to create web call and get access token
        console.log('🎤 Creating web call with agent:', this.voiceAgentData.retell_agent_id);
        const response = await axios.post('/api/client/voice-agent/create-web-call', {
          agent_id: this.voiceAgentData.retell_agent_id
        });

        console.log('🎤 Web call creation response:', response.data);

        if (response.data.flag === 1) {
          const { access_token } = response.data.data;
          console.log('✅ Access token received, starting call...');

          // Start the web call
          await this.retellWebClient.startCall({
            accessToken: access_token,
            sampleRate: 24000,
            captureDeviceId: "default",
            playbackDeviceId: "default",
            emitRawAudioSamples: false
          });

          this.isWebCallActive = true;
          this.webCallTranscript = [];
          console.log('✅ Voice call started successfully');
          this.$toast.success('Voice conversation started! You can now speak with your AI assistant.');
        } else {
          console.error('❌ Failed to create web call:', response.data);
          this.$toast.error(response.data.message || 'Failed to start voice call');
        }
      } catch (error) {
        console.error('❌ Error starting voice call:', error);

        if (error.message && error.message.includes('getUserMedia')) {
          this.$toast.error('Microphone access denied. Please allow microphone access and try again.');
        } else if (error.message && error.message.includes('NotAllowedError')) {
          this.$toast.error('Microphone permission denied. Please allow microphone access in your browser settings.');
        } else if (error.message && error.message.includes('NotFoundError')) {
          this.$toast.error('No microphone found. Please connect a microphone and try again.');
        } else if (error.response) {
          console.error('❌ API Error:', error.response.data);
          this.$toast.error('Server error: ' + (error.response.data.message || 'Failed to start voice call'));
        } else {
          this.$toast.error('Failed to start voice call: ' + error.message);
        }
      } finally {
        this.webCallLoading = false;
      }
    },

    stopWebCall() {
      try {
        if (this.retellWebClient && this.isWebCallActive) {
          this.retellWebClient.stopCall();
          this.isWebCallActive = false;
          this.isAgentSpeaking = false;
          this.$toast.success('Voice call ended');
        }
      } catch (error) {
        console.error('Error stopping voice call:', error);
        this.$toast.error('Error ending voice call');
      }
    },

    setupWebCallEventListeners() {
      if (!this.retellWebClient) {
        console.error('❌ Cannot setup event listeners: retellWebClient is null');
        return;
      }

      console.log('🎤 Setting up web call event listeners...');

      this.retellWebClient.on("call_started", () => {
        console.log("✅ Voice call started");
        this.isWebCallActive = true;
        this.$toast.success('Voice call connected! Start speaking...');
      });

      this.retellWebClient.on("call_ended", () => {
        console.log("🔚 Voice call ended");
        this.isWebCallActive = false;
        this.isAgentSpeaking = false;
        this.webCallTranscript = [];
      });

      this.retellWebClient.on("agent_start_talking", () => {
        console.log("🗣️ Agent started talking");
        this.isAgentSpeaking = true;
      });

      this.retellWebClient.on("agent_stop_talking", () => {
        console.log("🤐 Agent stopped talking");
        this.isAgentSpeaking = false;
      });

      this.retellWebClient.on("update", (update) => {
        console.log("📝 Voice call update:", update);
        if (update.transcript) {
          // Parse transcript and add to our display
          const transcript = update.transcript;
          if (transcript.length > 0) {
            console.log("📝 Updating transcript with", transcript.length, "messages");

            // Process transcript for emotion detection
            this.processTranscriptForEmotions(transcript);

            // Clear existing transcript and add new ones
            this.webCallTranscript = transcript.map(item => ({
              role: item.role,
              content: item.content
            }));
          }
        }
      });

      this.retellWebClient.on("error", (error) => {
        console.error("❌ Voice call error:", error);
        this.$toast.error('Voice call error: ' + (error.message || 'Unknown error'));
        this.stopWebCall();
      });

      // Additional event listeners for debugging
      this.retellWebClient.on("conversationStarted", () => {
        console.log("🎯 Conversation started");
      });

      this.retellWebClient.on("conversationEnded", () => {
        console.log("🎯 Conversation ended");
      });

      console.log('✅ Event listeners setup complete');
    },

    async testMicrophone() {
      this.micTestLoading = true;
      try {
        console.log('🎤 Testing microphone access...');

        // Check if we're on HTTPS (required for microphone access)
        if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
          this.$toast.error('Microphone access requires HTTPS. Please use a secure connection.');
          return;
        }

        // Test microphone access
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        console.log('✅ Microphone access granted');
        console.log('🎤 Audio tracks:', stream.getAudioTracks());

        // Test for a few seconds
        this.$toast.success('Microphone test successful! You can now use voice chat.');

        // Stop the test stream after 2 seconds
        setTimeout(() => {
          stream.getTracks().forEach(track => {
            track.stop();
            console.log('🔇 Stopped microphone test stream');
          });
        }, 2000);

      } catch (error) {
        console.error('❌ Microphone test failed:', error);

        if (error.name === 'NotAllowedError') {
          this.$toast.error('Microphone permission denied. Please allow microphone access in your browser settings and try again.');
        } else if (error.name === 'NotFoundError') {
          this.$toast.error('No microphone found. Please connect a microphone and try again.');
        } else if (error.name === 'NotReadableError') {
          this.$toast.error('Microphone is being used by another application. Please close other apps using the microphone.');
        } else if (error.name === 'OverconstrainedError') {
          this.$toast.error('Microphone constraints not supported. Please try with a different microphone.');
        } else {
          this.$toast.error('Microphone test failed: ' + error.message);
        }
      } finally {
        this.micTestLoading = false;
      }
    },

    /**
     * Process transcript for emotion detection and natural laughter triggers
     */
    processTranscriptForEmotions(transcript) {
      try {
        // Get the latest user message
        const userMessages = transcript.filter(item => item.role === 'user');
        if (userMessages.length === 0) return;

        const latestUserMessage = userMessages[userMessages.length - 1];
        const userText = latestUserMessage.content.toLowerCase();

        // Humor detection patterns
        const humorPatterns = [
          // Direct humor indicators
          /haha|hehe|lol|lmao|rofl|😂|😄|😆/i,

          // Self-deprecating humor about cars
          /my (car|vehicle|ride) is (so old|ancient|falling apart|a piece of junk|dying)/i,
          /i drive a (beater|clunker|rust bucket|dinosaur)/i,
          /my car (sounds like|makes noise like) (a tractor|dying animal|blender)/i,

          // Funny car-related situations
          /my car (broke down|died) (again|yesterday|last week)/i,
          /i need a car that (actually starts|won't embarrass me|my kids won't be ashamed of)/i,
          /looking for something that (runs|works|starts)/i,

          // Witty responses about budget
          /my budget is (basically nothing|whatever's in my couch cushions|monopoly money)/i,
          /i have (no money|broke|poor) but want (a ferrari|luxury|expensive)/i,

          // Funny questions or comments
          /do you have anything for someone who (can't drive|crashes a lot|is terrible at parking)/i,
          /what's the cheapest way to look rich/i,
          /can i trade my (kids|spouse|dog) for a car/i,

          // Sarcastic or ironic statements
          /oh great, another (salesperson|car dealer)/i,
          /let me guess, you have the perfect car for me/i,
          /i'm sure this won't be expensive at all/i
        ];

        // Check if user message contains humor
        const isHumorous = humorPatterns.some(pattern => pattern.test(userText));

        if (isHumorous) {
          console.log('😄 Humor detected in user message:', userText);

          // Log for analytics (could be sent to backend for learning)
          this.logHumorDetection(userText, 'pattern_match');

          // The agent will naturally respond with laughter based on the enhanced prompt
          // No need to inject artificial laughter here - Retell's TTS will handle it
        }

        // Additional context-based humor detection
        this.detectContextualHumor(userText, transcript);

      } catch (error) {
        console.error('Error processing transcript for emotions:', error);
      }
    },

    /**
     * Detect contextual humor based on conversation flow
     */
    detectContextualHumor(userText, _transcript) {
      try {
        // Look for timing-based humor (quick witty responses)
        // const conversationFlow = transcript.slice(-4); // Last 4 messages - for future use

        // Detect if user is making jokes about the sales process
        const salesProcessHumor = [
          /let me think about it/i, // Classic car buying response
          /i need to ask my (wife|husband|spouse|partner)/i,
          /what's the catch/i,
          /too good to be true/i,
          /where's the fine print/i
        ];

        const isSalesHumor = salesProcessHumor.some(pattern => pattern.test(userText));

        if (isSalesHumor) {
          console.log('😏 Sales process humor detected:', userText);
          this.logHumorDetection(userText, 'sales_process_humor');
        }

        // Detect storytelling humor (user sharing funny car experiences)
        if (userText.includes('story') || userText.includes('happened') || userText.includes('time when')) {
          const storyHumor = [
            /funny thing|hilarious|ridiculous|crazy thing/i,
            /you won't believe|believe it or not/i,
            /worst part was|best part was/i
          ];

          const isStoryHumor = storyHumor.some(pattern => pattern.test(userText));
          if (isStoryHumor) {
            console.log('📖 Story-based humor detected:', userText);
            this.logHumorDetection(userText, 'story_humor');
          }
        }

      } catch (error) {
        console.error('Error in contextual humor detection:', error);
      }
    },

    /**
     * Log humor detection for analytics and learning
     */
    logHumorDetection(userText, humorType) {
      try {
        // Could send to backend for analytics
        console.log('🎭 Humor Analytics:', {
          type: humorType,
          text: userText.substring(0, 100), // First 100 chars for privacy
          timestamp: new Date().toISOString(),
          session_id: this.sessionId
        });

        // Optional: Send to backend for learning and improvement
        // axios.post('/api/voice-agent/humor-analytics', {
        //   humor_type: humorType,
        //   detected_at: new Date().toISOString(),
        //   session_id: this.sessionId
        // }).catch(error => console.log('Analytics logging failed:', error));

      } catch (error) {
        console.error('Error logging humor detection:', error);
      }
    }
  }
}
</script>

<style scoped>
.digital-assistant-wrapper {
  position: relative;
}

.chat-toggle-btn {
  box-shadow: 0 4px 16px rgba(0,0,0,0.3) !important;
}

.chat-toggle-container {
  position: relative;
}

.online-indicator {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

.online-indicator-header {
  animation: pulse-small 2s infinite;
}

@keyframes pulse-small {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

.chat-window {
  border-radius: 12px !important;
  overflow: hidden;
}

.chat-header {
  border-radius: 12px 12px 0 0 !important;
}

.messages-container {
  background: white;
}

.message-bubble {
  word-wrap: break-word;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.message-text {
  line-height: 1.4;
}

/* Style for regular clickable links in messages */
.message-text >>> .regular-link {
  color: #1976d2 !important;
  text-decoration: underline !important;
  transition: color 0.2s ease;
}

.message-text >>> .regular-link:hover {
  color: #1565c0 !important;
  text-decoration: underline !important;
}

/* Special styling for agreement links */
.message-text >>> .agreement-link {
  display: inline-block;
  color: #ff5722 !important;
  font-weight: bold !important;
  background-color: #fff3e0 !important;
  padding: 6px 12px !important;
  border-radius: 8px !important;
  border: 2px solid #ff5722 !important;
  text-decoration: none !important;
  margin: 4px 0 !important;
  transition: all 0.3s ease !important;
  cursor: pointer !important;
}

.message-text >>> .agreement-link:hover {
  background-color: #ff5722 !important;
  color: white !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 87, 34, 0.4) !important;
}

.typing-indicator {
  padding: 8px 12px !important;
}

.typing-dots {
  display: flex;
  gap: 4px;
}

.typing-dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #666;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-dots span:nth-child(1) { animation-delay: -0.32s; }
.typing-dots span:nth-child(2) { animation-delay: -0.16s; }

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.chat-input .v-text-field {
  font-size: 14px;
}

.chat-input .v-text-field .v-input__control .v-input__slot {
  background-color: white !important;
  border: 2px solid #e0e0e0 !important;
  border-radius: 8px !important;
}

.chat-input .v-text-field.v-input--is-focused .v-input__control .v-input__slot {
  border-color: #ff9800 !important;
  box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.2) !important;
}

.chat-window {
  box-shadow: 0 8px 32px rgba(0,0,0,0.3) !important;
  border: 1px solid #e0e0e0;
}

.message-bubble {
  word-wrap: break-word;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  border-radius: 12px !important;
}

.chat-toggle-btn {
  box-shadow: 0 4px 16px rgba(255, 152, 0, 0.4) !important;
  transition: all 0.3s ease;
}

.chat-toggle-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.6) !important;
}

/* Full Page Mode Styles */
.full-page-mode {
  width: 100% !important;
  height: 100% !important;
  position: relative !important;
  bottom: auto !important;
  right: auto !important;
  box-shadow: none !important;
  border: none !important;
}

.full-page-mode .chat-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.full-page-mode .message-bubble {
  max-width: 70%;
  padding: 16px 20px;
  margin-bottom: 8px;
}

.full-page-mode .ai-message {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  color: #333;
  border-bottom-left-radius: 4px !important;
}

.full-page-mode .user-message {
  color: white;
  border-bottom-right-radius: 4px !important;
}

.full-page-mode .message-time {
  font-size: 0.75rem;
  opacity: 0.7;
  margin-top: 4px;
}

.full-page-mode .chat-input {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
}

.full-page-mode .typing-indicator {
  background: #f0f0f0;
  padding: 12px 16px;
  border-radius: 12px;
  border-bottom-left-radius: 4px;
}

/* Financing option styling */
.financing-option-top {
  position: sticky;
  top: 0;
  z-index: 5;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.financing-option-top-fullpage {
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.financing-btn {
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
}

.financing-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4) !important;
}

.financing-btn-fullpage {
  transition: all 0.3s ease !important;
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.3) !important;
}

.financing-btn-fullpage:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4) !important;
}

/* Responsive adjustments for mobile */
@media (max-width: 768px) {
  .full-page-mode .message-bubble {
    max-width: 85%;
    padding: 12px 16px;
  }

  .financing-option-top {
    padding: 8px !important;
  }

  .financing-option-top-fullpage {
    padding: 12px !important;
  }

  .financing-btn {
    font-size: 13px !important;
    padding: 8px 12px !important;
  }

  .financing-btn-fullpage {
    font-size: 14px !important;
    padding: 12px 16px !important;
  }

  /* Chat container height adjustments for mobile */
  .messages-container {
    height: 280px !important; /* Smaller for mobile with financing button */
  }

  .full-page-mode .messages-container {
    height: calc(100vh - 200px) !important; /* Adjusted for financing button */
  }

  /* Fullscreen modal adjustments */
  .v-dialog--fullscreen .v-card {
    height: 100vh !important;
  }
}

/* Popup Chat Window Layout */
.chat-window.popup-mode {
  background: white;
  border-radius: 12px;
  overflow: hidden;
}

.chat-window.popup-mode .chat-header {
  flex-shrink: 0;
}

.chat-window.popup-mode .chat-messages {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.chat-window.popup-mode .voice-mode-toggle,
.chat-window.popup-mode .chat-input {
  flex-shrink: 0;
}

/* Voice Mode Styles */
.voice-mode-toggle {
  border-top: 1px solid #e0e0e0;
}

.voice-mode-toggle-fullpage {
  border-top: 1px solid #e0e0e0;
}

.voice-controls, .voice-controls-fullpage {
  min-height: 60px;
}

.voice-transcript {
  border-top: 1px solid #e0e0e0;
  background-color: #fafafa;
}

.voice-transcript-fullpage {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  padding: 16px;
}

.transcript-container {
  background-color: white;
  border-radius: 8px;
  padding: 8px;
  border: 1px solid #e0e0e0;
}

.transcript-container-fullpage {
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.transcript-message, .transcript-message-fullpage {
  margin-bottom: 4px;
}

.transcript-message:last-child, .transcript-message-fullpage:last-child {
  margin-bottom: 0;
}

/* Voice button animations */
.v-btn--loading .v-btn__content {
  opacity: 0;
}

/* Voice mode toggle button styling */
.v-btn-toggle .v-btn {
  border: 1px solid #e0e0e0 !important;
}

.v-btn-toggle .v-btn.orange {
  border-color: #ff9800 !important;
}
</style>
